<template>
  <div>
    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Error Snackbar -->
    <v-snackbar v-model="showError" color="error" timeout="3000">
      {{ errorMessage }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="showError = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Back Button Header -->
    <div
      style="background-color: #ffffff; border-radius: 4px; padding: 12px 20px; margin: 16px 24px 0 24px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); border: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center;">
      <div style="display: flex; align-items: center; gap: 10px;">
        <v-icon size="18" color="#555">mdi-view-dashboard</v-icon>
        <span
          style="font-weight: 700; font-size: 15px; color: #444; letter-spacing: 0.5px; text-transform: uppercase;">DEALER
          DETAILS</span>
      </div>
      <v-btn variant="flat"
        style="background-color: #800080 !important; color: #ffffff !important; border-radius: 4px !important;"
        @click="goBack" :disabled="loading">
        CLOSE
      </v-btn>
    </div>

    <v-card class="pa-6 mx-6 custom_card_box mt-4" border="sm opacity-1">
      <v-form readonly>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field v-model="companyName" label="Company/Institute Name" variant="outlined" density="comfortable"
              readonly class="custom_input_field_class_view mb-4" />
            <v-text-field v-model="dealerName" label="Dealer Name" variant="outlined" density="comfortable" readonly
              class="custom_input_field_class_view mb-4" />
            <v-text-field v-model="dealerCode" label="Dealer Code" variant="outlined" density="comfortable" readonly
              class="custom_input_field_class_view mb-4" />

            <!-- Add Dealer Type Field Here -->
            <v-text-field v-model="dealerType" label="Dealer Type" variant="outlined" density="comfortable" readonly
              class="custom_input_field_class_view mb-4" />

            <!-- Display latitude and longitude -->
            <v-text-field v-model="latitude" label="Latitude" variant="outlined" density="comfortable" readonly
              class="custom_input_field_class_view mb-4" />
            <v-text-field v-model="longitude" label="Longitude" variant="outlined" density="comfortable" readonly
              class="custom_input_field_class_view mb-4" />

            <div v-if="phoneNumbers.length > 0">
              <div class="d-flex align-center mb-4" v-for="(phone, index) in phoneNumbers" :key="index">
                <v-icon size="x-small" class="mr-3">mdi-circle</v-icon>
                <v-text-field :model-value="phone.displayNumber"
                  :label="index === 0 ? 'Primary Phone Number' : 'Secondary Phone Number'" variant="outlined"
                  density="comfortable" hide-details readonly class="custom_input_field_class_view flex-grow-1" />
              </div>
            </div>
            <div v-else class="text-center text-grey pa-4 mb-4">
              No phone numbers available
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div v-if="piyestraCompanies.length > 0">
              <div v-for="(company, index) in piyestraCompanies" :key="index" class="mb-4">
                <v-card variant="outlined" class="pa-3 mb-2">
                  <v-card-title class="text-subtitle-2 font-weight-bold pa-0 mb-2">{{ company.company || 'N/A'
                    }}</v-card-title>
                  <v-row dense>
                    <v-col cols="6">
                      <v-text-field :model-value="formatCurrency(company.credit_limit_type)" label="Credit Limit"
                        variant="outlined" density="comfortable" readonly hide-details
                        class="custom_input_field_class_view" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field :model-value="formatDays(company.credit_period_type)" label="Credit Period"
                        variant="outlined" density="comfortable" readonly hide-details
                        class="custom_input_field_class_view" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field :model-value="formatCurrency(company.cash_limit_type)" label="Cash Limit"
                        variant="outlined" density="comfortable" readonly hide-details
                        class="custom_input_field_class_view" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field :model-value="formatDays(company.cash_period_type)" label="Cash Period"
                        variant="outlined" density="comfortable" readonly hide-details
                        class="custom_input_field_class_view" />
                    </v-col>
                    <!-- <v-col cols="12">
                      <v-text-field 
                        :model-value="company.rep_code" 
                        label="Rep Code" 
                        variant="outlined" 
                        density="comfortable" 
                        readonly 
                        hide-details
                        class="custom_input_field_class_view" 
                      />
                    </v-col> -->
                  </v-row>
                </v-card>
              </div>
            </div>
            <div v-else class="text-center text-grey pa-4">
              No company details available
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <p class="text-subtitle-1 font-weight-bold mb-4">DEALER LOCATION</p>
        <v-row>
          <v-col cols="12" md="6">
            <div class="map-container">
              <Map ref="map" :center="mapCenter" :zoom="15" style="height: 300px; width: 100%;"
                class="custom_card_box rounded-lg overflow-hidden">
                <Marker v-if="markerPosition" :icon="customIcon" :position="markerPosition" />
              </Map>
            </div>
            <div class="text-center mt-2">
              <v-chip v-if="markerPosition" color="primary" size="small">
                📍 Lat: {{ displayLatitude }} | Lng: {{ displayLongitude }}
              </v-chip>
              <v-chip v-else color="grey" size="small">
                📍 No location selected
              </v-chip>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field v-model="outletAddress" label="Address" variant="outlined" density="comfortable" readonly
              class="custom_input_field_class_view mb-4" />
            <v-text-field v-model="regionCode" label="Region Code" variant="outlined" density="comfortable" readonly
              class="custom_input_field_class_view mb-4" />
            <v-text-field v-model="areaCode" label="Area Code" variant="outlined" density="comfortable" readonly
              class="custom_input_field_class_view mb-4" />
          </v-col>
        </v-row>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import { Map, Marker } from '@fawmi/vue-google-maps';
import { getPermissions } from "@/plugins/permissions";

export default {
  name: "OutletDetail",
  components: {
    Map,
    Marker,
  },
  props: {
    dealerId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_API_BASE_URL,
      loading: false,
      showError: false,
      errorMessage: '',
      companyName: '',
      dealerName: '',
      dealerCode: '',
      dealerType: '', // Add dealerType to data
      latitude: '',
      longitude: '',
      displayLatitude: '',
      displayLongitude: '',
      piyestraCompanies: [],
      outletAddress: '',
      regionCode: '',
      areaCode: '',
      phoneNumbers: [],
      mapCenter: { lat: 6.9271, lng: 79.8612 }, // Default Sri Lanka
      markerPosition: null,
      customIcon: {
        url: require('@/assets/ICONS/maker.png'), // Use the same path as working page
        scaledSize: { width: 35, height: 40 }
      },
      permissions: {},
      abortController: null,
      mapMarkers: [] // Store markers for cleanup
    };
  },

  computed: {
    ...mapGetters(['sharedDataOutlet']),

    // Get dealer ID from props or route or store
    currentDealerId() {
      return this.dealerId || this.$route.query.id || this.sharedDataOutlet;
    }
  },

  watch: {
    currentDealerId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchOutletRecodeData();
        }
      }
    }
  },

  mounted() {
    const dashboardId = 9;
    this.permissions = getPermissions(dashboardId);

    // Initialize map when component is mounted - similar to working page
    if (this.$refs.map) {
      this.$refs.map.$mapPromise.then(map => {
        console.log('Map loaded successfully');
        this.initMapEvents(map);
      }).catch(err => {
        console.log('Map not available:', err);
      });
    }
  },

  methods: {
    goBack() {
      // Navigate back to the previous page or dealer list
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/dealer-list');
      }
    },

    formatCurrency(value) {
      if (!value && value !== 0) return 'Rs. 0';
      return `Rs. ${parseFloat(value).toLocaleString()}`;
    },

    formatDays(value) {
      if (!value && value !== 0) return '0 days';
      return `${value} days`;
    },

    async fetchOutletRecodeData() {
      // Cancel previous request if exists
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      const token = localStorage.getItem('token');
      const dealerId = this.currentDealerId;

      if (!dealerId) {
        this.showErrorNotification('No dealer ID provided');
        return;
      }

      if (!token) {
        this.showErrorNotification('Authentication token not found');
        return;
      }

      this.loading = true;
      this.showError = false;

      try {
        const response = await axios.get(
          `${this.baseURL}/api/getDealerById/${dealerId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: this.abortController.signal
          }
        );

        if (response.data && response.data.success && response.data.data) {
          this.processDealerData(response.data.data);
        } else {
          throw new Error(response.data?.message || 'Invalid response from server');
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dealer data:", error);
          this.showErrorNotification(error.message || 'Failed to fetch dealer data');
        }
      } finally {
        this.loading = false;
        this.abortController = null;
      }
    },

    processDealerData(dealerData) {
      // Basic dealer information
      this.companyName = dealerData.company_name || '';
      this.dealerName = dealerData.dealer_name || '';
      this.dealerCode = dealerData.dealer_code || '';
      this.dealerType = dealerData.dealer_type || 'N/A'; // Set dealer type from API response

      // Address and location
      this.outletAddress = dealerData.address || '';
      this.regionCode = dealerData.region_code || '';
      this.areaCode = dealerData.area_code || '';

      // Latitude and Longitude - using lat and lng from API response
      if (dealerData.lat && dealerData.lng) {
        const lat = parseFloat(dealerData.lat);
        const lng = parseFloat(dealerData.lng);

        if (!isNaN(lat) && !isNaN(lng)) {
          this.latitude = dealerData.lat;
          this.longitude = dealerData.lng;
          this.displayLatitude = lat.toFixed(6);
          this.displayLongitude = lng.toFixed(6);
          this.mapCenter = { lat, lng };
          this.markerPosition = { lat, lng };

          // Update map marker after map is loaded
          this.updateMapMarker();
        } else {
          console.warn('Invalid coordinates:', dealerData.lat, dealerData.lng);
        }
      } else {
        console.warn('No coordinates found in dealer data');
        this.latitude = 'Not available';
        this.longitude = 'Not available';
        this.displayLatitude = 'Not available';
        this.displayLongitude = 'Not available';
      }

      // Phone numbers - handling both mobile_number and mobile_number2
      const phones = [];
      if (dealerData.mobile_number) {
        phones.push({
          displayNumber: this.formatPhoneNumber(dealerData.mobile_number)
        });
      }
      if (dealerData.mobile_number2) {
        phones.push({
          displayNumber: this.formatPhoneNumber(dealerData.mobile_number2)
        });
      }
      this.phoneNumbers = phones;

      // Piyestra companies data - now includes rep_code
      if (dealerData.companies && Array.isArray(dealerData.companies)) {
        this.piyestraCompanies = dealerData.companies.map(company => ({
          ...company,
          rep_code: company.rep_code || 'N/A' // Ensure rep_code is available
        }));
      } else {
        this.piyestraCompanies = [];
      }
    },

    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return '';
      // Format Sri Lankan phone numbers
      if (phoneNumber.startsWith('0')) {
        return `+94 ${phoneNumber.substring(1)}`;
      }
      return phoneNumber;
    },

    showErrorNotification(message) {
      this.errorMessage = message;
      this.showError = true;
    },

    initMapEvents(map) {
      // Clear existing markers
      this.clearMapMarkers();

      // Add marker if position exists
      if (this.markerPosition) {
        const marker = new window.google.maps.Marker({
          map: map,
          icon: this.customIcon,
          position: this.markerPosition,
          animation: window.google.maps.Animation.DROP
        });
        this.mapMarkers.push(marker);

        // Center map on marker
        map.setCenter(this.markerPosition);
        map.setZoom(15);
      }
    },

    updateMapMarker() {
      if (this.$refs.map && this.markerPosition) {
        this.$refs.map.$mapPromise.then(map => {
          this.clearMapMarkers();

          const marker = new window.google.maps.Marker({
            map: map,
            icon: this.customIcon,
            position: this.markerPosition,
            animation: window.google.maps.Animation.DROP
          });
          this.mapMarkers.push(marker);

          // Center map on marker
          map.setCenter(this.markerPosition);
          map.setZoom(15);
        }).catch(err => {
          console.log('Map not ready:', err);
        });
      }
    },

    clearMapMarkers() {
      this.mapMarkers.forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      this.mapMarkers = [];
    }
  },

  beforeUnmount() {
    // Cancel any pending requests
    if (this.abortController) {
      this.abortController.abort();
    }
    // Clear map markers
    this.clearMapMarkers();
  }
};
</script>

<style scoped>
@import "../../styles/outlet_management/outlet_list.scss";

.custom_input_field_class_view {
  pointer-events: none;
}

.map-container {
  height: 300px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

/* Loading overlay styles */
:deep(.v-overlay) {
  z-index: 1000;
}
</style>